import { ReactNode, useEffect, useMemo } from 'react';

// material-ui
import { createTheme, StyledEngineProvider, Theme, ThemeOptions, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// emotion RTL cache (C-1)
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import rtlPlugin from 'stylis-plugin-rtl';

// project imports
import { useLocale } from '@zercode/i18n';
import { BrandingProvider } from './BrandingContext';
import { applyCustomCss } from './customCss';
import { applyBrandingToPalettes, SchemePalettes } from './palette';
import { TenantBranding } from './types';

// C-1 RTL: one cache per direction, created once. The RTL cache runs stylis-plugin-rtl so MUI's
// logical CSS is flipped for Arabic; LTR uses the plain MUI cache (Berry RTLLayout pattern).
const ltrCache = createCache({ key: 'mui' });
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [rtlPlugin] });

/**
 * Berry seam [STD-FE]: Berry is WRAPPED, never forked. The Berry source lives
 * under each app's `src/berry` and the app injects Berry's own theme builders
 * here. This provider re-assembles Berry's `themes/index.tsx` composition
 * (buildPalette → typography → customShadows → componentsOverrides) but feeds
 * it from the CONTROL PLANE TENANT BRANDING instead of static presets /
 * localStorage user prefs.
 */
export interface BerrySeam {
  /** Berry's `buildPalette(presetColor)` output used as the base palettes */
  buildBasePalette: () => SchemePalettes;
  /** Berry's `Typography(fontFamily)` factory */
  typography?: (fontFamily: string) => Record<string, unknown>;
  /** Berry's `CustomShadows(palette, mode)` factory */
  customShadows?: (palette: Record<string, unknown>, mode: 'light' | 'dark') => Record<string, unknown>;
  /** Berry's `componentsOverrides(theme, borderRadius, outlinedFilled)` */
  componentsOverrides?: (theme: Theme, borderRadius: number, outlinedFilled: boolean) => Theme['components'];
}

export interface ZercodeThemeProviderProps {
  /** resolved BEFORE first paint — see resolveBrandingBeforePaint() [STD-FE] */
  branding: TenantBranding;
  berry: BerrySeam;
  outlinedFilled?: boolean;
  /** explicit override; when omitted the tenant's `defaultColorScheme` from branding applies */
  defaultMode?: 'light' | 'dark' | 'system';
  children: ReactNode;
}

/**
 * First-paint color scheme: tenant branding's `defaultColorScheme` decides the
 * DEFAULT mode [STD-FE]; an explicit `defaultMode` prop wins over branding.
 * (A user's own persisted toggle, stored by MUI under `zercode-theme-mode`,
 * still wins over the default — it is a default, not a lock.)
 */
export function resolveDefaultMode(branding: TenantBranding, override?: 'light' | 'dark' | 'system'): 'light' | 'dark' | 'system' {
  return override ?? branding.defaultColorScheme ?? 'light';
}

export function ZercodeThemeProvider({ branding, berry, outlinedFilled = true, defaultMode, children }: ZercodeThemeProviderProps) {
  const borderRadius = branding.borderRadius ?? 8;
  // Font is tenant branding (Settings ▸ Branding), never a per-user preference.
  const fontFamily = branding.typography.fontFamily ?? `'Roboto', sans-serif`;
  // C-1: theme direction follows the ACTIVE LOCALE (ar ⇒ rtl). useLocale reads the global i18n
  // store, so this works regardless of provider nesting; changing locale re-themes + swaps cache.
  const { direction } = useLocale();

  // C-7 Enterprise: inject the tenant's sanitized custom CSS live (updates when branding changes;
  // removed when cleared). Sanitization happens inside applyCustomCss — the security boundary.
  useEffect(() => {
    applyCustomCss(branding.customCss);
  }, [branding.customCss]);

  const theme: Theme = useMemo(() => {
    // Berry base palettes + tenant brand colors from Control Plane branding
    const palettes = applyBrandingToPalettes(berry.buildBasePalette(), branding.colors);

    const themeOptions = {
      mixins: {
        toolbar: {
          minHeight: '48px',
          padding: '16px',
          '@media (min-width: 600px)': { minHeight: '48px' }
        }
      },
      direction,
      typography: berry.typography ? berry.typography(fontFamily) : { fontFamily },
      colorSchemes: {
        light: {
          palette: palettes.light,
          ...(berry.customShadows ? { customShadows: berry.customShadows(palettes.light, 'light') } : {})
        },
        dark: {
          palette: palettes.dark,
          ...(berry.customShadows ? { customShadows: berry.customShadows(palettes.dark, 'dark') } : {})
        }
      },
      shape: { borderRadius },
      cssVariables: {
        cssVarPrefix: '',
        colorSchemeSelector: 'data-color-scheme'
      }
      // cast: Berry's module augmentations (customShadows, palette 200/800 …)
      // are declared app-side in src/berry/types/overrides
    } as unknown as ThemeOptions;

    const built = createTheme(themeOptions);
    if (berry.componentsOverrides) {
      built.components = berry.componentsOverrides(built, borderRadius, outlinedFilled);
    }
    return built;
  }, [berry, branding.colors, borderRadius, direction, fontFamily, outlinedFilled]);

  return (
    <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
      <BrandingProvider branding={branding}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider
            disableTransitionOnChange
            theme={theme}
            modeStorageKey="zercode-theme-mode"
            defaultMode={resolveDefaultMode(branding, defaultMode)}
          >
            <CssBaseline enableColorScheme />
            {children}
          </ThemeProvider>
        </StyledEngineProvider>
      </BrandingProvider>
    </CacheProvider>
  );
}
